import type { Mutation } from '@familyhub/shared';
import { categoryOrderKey, db } from '.';
import { stamp } from './clock';

/**
 * The outbox (ARCHITECTURE §11).
 *
 * Every change this client makes takes the same route: apply it locally so the
 * screen updates instantly, queue it, and let the sync engine replay the queue
 * **in order** through `POST /sync`. There is no second, online-only write path
 * — the REST endpoints in API §5 exist and do the same thing, but choosing
 * between them per action would give the client two behaviours to keep in step,
 * which is the failure mode REVIEW G6 is about on the server side.
 *
 * The local apply below is **optimistic and not authoritative**. It mirrors the
 * server's rules closely enough that the screen does not flicker, and the next
 * pull overwrites whatever it got wrong. Where the two disagree, the server
 * wins by construction: its rows arrive with their own `updated_at`.
 */

export async function enqueue(mutation: Mutation): Promise<void> {
  await db.transaction('rw', [db.list_items, db.generic_products, db.product_variants, db.store_category_order, db.outbox], async () => {
    await applyLocal(mutation);
    await db.outbox.add({ mutation, queued_at: new Date().toISOString() });
  });
}

/** Newer wins, per row — never per field (DATABASE §12). */
function isNewer(incoming: string, stored: string | undefined): boolean {
  if (!stored) return true;
  return Date.parse(incoming) > Date.parse(stored);
}

async function applyLocal(m: Mutation): Promise<void> {
  if (m.op === 'checkoff') {
    // Check-off is not a row write and is exempt from last-write-wins. The item
    // is tombstoned, not marked bought — there is no state column, and the
    // purchase is the permanent record (API §5.2).
    const item = await db.list_items.get(m.list_item_id);
    if (item) {
      await db.list_items.put({
        ...item,
        deleted_at: item.deleted_at ?? stamp(),
        updated_at: stamp(),
      });
    }
    return;
  }

  if (m.op === 'delete') {
    if (m.table !== 'list_items' && m.table !== 'generic_products') return;
    const table = m.table === 'list_items' ? db.list_items : db.generic_products;
    const existing = await table.get(m.row.id);
    if (!existing) return;
    // Soft, always. A tombstone is what propagates the delete to the other
    // phone instead of the row being resurrected by its next push.
    await table.put({
      ...existing,
      deleted_at: existing.deleted_at ?? m.row.updated_at,
      updated_at: m.row.updated_at,
    } as never);
    return;
  }

  switch (m.table) {
    case 'list_items': {
      const existing = await db.list_items.get(m.row.id);
      if (existing && !isNewer(m.row.updated_at, existing.updated_at)) return;
      await db.list_items.put({
        id: m.row.id,
        store_id: m.row.store_id ?? null,
        generic_product_id: m.row.generic_product_id,
        variant_id: m.row.variant_id ?? null,
        quantity: m.row.quantity,
        note: m.row.note ?? null,
        added_by_user: m.row.added_by_user,
        suggestion_reason: m.row.suggestion_reason ?? null,
        updated_at: m.row.updated_at,
        // Sticky, exactly as on the server: an edit carrying deleted_at: null
        // must not resurrect an item that was already bought.
        deleted_at: existing?.deleted_at ?? m.row.deleted_at ?? null,
      });
      return;
    }

    case 'generic_products': {
      const existing = await db.generic_products.get(m.row.id);
      if (existing && !isNewer(m.row.updated_at, existing.updated_at)) return;
      await db.generic_products.put({
        id: m.row.id,
        name: m.row.name,
        slug: m.row.slug,
        category: m.row.category ?? null,
        category_source: m.row.category_source ?? 'auto',
        updated_at: m.row.updated_at,
        deleted_at: existing?.deleted_at ?? m.row.deleted_at ?? null,
      });
      return;
    }

    case 'store_category_order': {
      await db.store_category_order.put({
        key: categoryOrderKey(m.row.store_id, m.row.category),
        store_id: m.row.store_id,
        category: m.row.category,
        position: m.row.position,
        updated_at: m.row.updated_at,
      });
      return;
    }

    case 'product_variants': {
      // Created by selection, which is online-only; the one edit a phone
      // makes itself is hiding a product from "Jullie producten".
      const existing = await db.product_variants.get(m.row.id);
      if (!existing || !isNewer(m.row.updated_at, existing.updated_at)) return;
      await db.product_variants.put({
        ...existing,
        hidden: m.row.hidden ?? existing.hidden,
        updated_at: m.row.updated_at,
      });
      return;
    }

    default:
      // variant_attributes are written by selection, an online-only call.
      return;
  }
}

export async function outboxSize(): Promise<number> {
  return db.outbox.count();
}
